//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import PondTypeModel from './pondType.model';

/**
 * Service for managing pond types data (Master Data).
 *
 * Provides methods to fetch and manage pond types-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module PondTypesService
 */
@Injectable({
  providedIn: 'root'
})
export class PondTypesService extends Service<PondTypeModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of PondTypesService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/pondtype/');
  }

  /**
   * Fetches the complete list of pond types (Master Data).
   *
   * @method
   * @returns {Promise<Array<PondTypeModel>>} A Promise resolving with an array of PondTypeModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link PondTypesComponent} to retrieve the full pond types list.
   */
  public getAllPondTypesList(): Promise<Array<PondTypeModel>> {
    return new Promise<Array<PondTypeModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<PondTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
